"use client";

import React, { useState } from "react";
import {
  Box,
  Button,
  Typography,
  CircularProgress,
  Stack,
  Tooltip,
  useMediaQuery,
} from "@mui/material";
import { AutoAwesome, Refresh } from "@mui/icons-material";
import ConfirmDialog from "@/app/components/modals/ConfirmDialog";
import { useTheme } from "@mui/material/styles";

const ReviewWritingAssistant = ({
  currentReview,
  onUseContent,
  rating,
  sharerName,
  meetingSummary = null,
}) => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const getPrompt = () => {
    const hasContent = currentReview && currentReview.trim().length > 0;
    const hasSummary = meetingSummary && meetingSummary.trim().length > 0;

    if (hasContent) {
      // Improve existing review
      return `Improve and refine this meeting review:

"${currentReview}"

Rating given: ${rating} out of 5 stars
Meeting with: ${sharerName}
${hasSummary ? `Meeting summary: ${meetingSummary}` : ""}

Write 2 different improved versions that:
- Keep the same core message and sentiment
- Sound more natural and authentic
- Are polished and professional
- Stay under 500 characters each
- Match the ${rating}-star rating tone

Only improve the existing review. Do not add information not mentioned.`;
    }

    // Generate from scratch
    if (hasSummary) {
      return `Write a thoughtful meeting review based on this meeting summary:

Meeting with: ${sharerName}
Rating: ${rating} out of 5 stars
Meeting summary: ${meetingSummary}

Write 2 different review options that:
- Reference specific topics or insights from the meeting
- Sound authentic and conversational
- Match the ${rating}-star rating sentiment
- Stay under 500 characters each
- Feel genuine and personal

Focus on what made the meeting valuable and what was appreciated.`;
    }

    // No summary available
    return `Write a thoughtful meeting review for this context:

Meeting with: ${sharerName}
Rating: ${rating} out of 5 stars

Write 2 different review options that:
- Sound authentic and conversational
- Match the ${rating}-star rating sentiment (${getRatingDescription(rating)})
- Stay under 500 characters each
- Feel genuine and personal
- Focus on professional value and insights

Provide helpful, specific appreciation appropriate for a ${rating}-star meeting.`;
  };

  const getRatingDescription = (rating) => {
    const descriptions = {
      1: "Poor - significant issues",
      2: "Fair - below expectations",
      3: "Good - met expectations",
      4: "Very Good - exceeded expectations",
      5: "Excellent - outstanding experience",
    };
    return descriptions[rating] || "Good";
  };

  const cleanSuggestionText = (text) => {
    return text
      .replace(/^\d+\.?\s*/, "")
      .replace(/^[^:]*:\s*/, "")
      .replace(/^["']|["']$/g, "")
      .replace(/^"|"$/g, "")
      .replace(/^'|'$/g, "")
      .trim();
  };

  const generateSuggestions = async () => {
    if (rating === 0) {
      return; // Don't generate without a rating
    }

    setIsGenerating(true);
    const hasExistingContent = currentReview && currentReview.trim().length > 0;

    try {
      const response = await fetch("/api/ai/review-writer", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          prompt: getPrompt(),
          rating,
          isImproving: hasExistingContent,
          sharerName,
          meetingSummary: meetingSummary || null,
        }),
      });

      if (!response.ok) throw new Error("Failed to generate suggestions");

      const reader = response.body?.getReader();
      const decoder = new TextDecoder();
      let content = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        content += decoder.decode(value);
      }

      const newSuggestions = parseAIResponse(content);
      const finalSuggestions = newSuggestions.slice(0, 2);

      if (finalSuggestions.length === 0) {
        const fallbackSuggestions = [content.trim()];
        setSuggestions(fallbackSuggestions);
      } else {
        setSuggestions(finalSuggestions);
      }

      setShowModal(true);
    } catch (error) {
      setSuggestions([
        "Sorry, I couldn't generate suggestions right now. Please try again.",
      ]);
      setShowModal(true);
    } finally {
      setIsGenerating(false);
    }
  };

  const parseAIResponse = (content) => {
    const suggestions = [];

    const lines = content.split("\n").filter((line) => line.trim());

    lines.forEach((line) => {
      if (line.match(/^\d+\.?\s+/)) {
        const cleanedText = cleanSuggestionText(line);
        if (
          cleanedText &&
          cleanedText.length > 10 &&
          cleanedText.length <= 500
        ) {
          suggestions.push(cleanedText);
        }
      } else if (line.includes(":") && line.length > 20) {
        const cleanedText = cleanSuggestionText(line);
        if (
          cleanedText &&
          cleanedText.length > 10 &&
          cleanedText.length <= 500
        ) {
          suggestions.push(cleanedText);
        }
      }
    });

    if (suggestions.length === 0) {
      const paragraphs = content.split("\n\n").filter((p) => p.trim());
      paragraphs.forEach((paragraph) => {
        const cleanedText = cleanSuggestionText(paragraph);
        if (
          cleanedText &&
          cleanedText.length > 10 &&
          cleanedText.length <= 500
        ) {
          suggestions.push(cleanedText);
        }
      });
    }

    return suggestions;
  };

  const handleSelectSuggestion = (suggestion) => {
    onUseContent(suggestion);
    setShowModal(false);
  };

  const handleRegenerateInModal = async () => {
    setIsGenerating(true);
    const hasExistingContent = currentReview && currentReview.trim().length > 0;

    try {
      const response = await fetch("/api/ai/review-writer", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          prompt: getPrompt(),
          rating,
          isImproving: hasExistingContent,
          sharerName,
          meetingSummary: meetingSummary || null,
        }),
      });

      if (!response.ok) throw new Error("Failed to generate suggestions");

      const reader = response.body?.getReader();
      const decoder = new TextDecoder();
      let content = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        content += decoder.decode(value);
      }

      const newSuggestions = parseAIResponse(content);
      const finalSuggestions = newSuggestions.slice(0, 2);

      if (finalSuggestions.length === 0) {
        setSuggestions([
          "Unable to generate new suggestions. Please try again.",
        ]);
      } else {
        setSuggestions(finalSuggestions);
      }
    } catch (error) {
      setSuggestions(["Error generating suggestions. Please try again."]);
    } finally {
      setIsGenerating(false);
    }
  };

  const modalBody = (
    <Box
      sx={{
        py: 2,
        maxHeight: "60vh",
        overflowY: "auto",
        overflowX: "hidden",
        borderRadius: "0px !important",
      }}
    >
      {suggestions.length === 0 && !isGenerating && (
        <Typography
          variant="body2"
          sx={{
            color: "text.secondary",
            textAlign: "center",
          }}
        >
          No suggestions available. Try clicking &quot;Generate New
          Suggestions&quot; below.
        </Typography>
      )}
      {suggestions.length > 0 && (
        <>
          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
              mb: 3,
            }}
          >
            Click any suggestion to use it immediately:
          </Typography>

          <Stack spacing={2}>
            {suggestions.map((suggestion, index) => (
              <Box
                key={index}
                sx={{
                  p: 3,
                  border: "2px solid",
                  borderColor:
                    hoveredIndex === index ? "primary.main" : "divider",
                  borderRadius: "16px",
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                  flexShrink: 0,
                  "&:hover": {
                    borderColor: "primary.main",
                    backgroundColor: "transparent",
                  },
                }}
                onClick={() => handleSelectSuggestion(suggestion)}
                onMouseEnter={() => setHoveredIndex(index)}
                onMouseLeave={() => setHoveredIndex(null)}
              >
                <Typography
                  variant="body2"
                  sx={{
                    lineHeight: 1.5,
                    opacity: hoveredIndex === index ? 1 : 0.7,
                  }}
                >
                  {suggestion}
                </Typography>

                <Typography
                  variant="caption"
                  sx={{
                    color: "text.secondary",
                    mt: 1,
                    display: "block",
                    opacity: hoveredIndex === index ? 1 : 0.7,
                    transition: "opacity 0.2s ease",
                  }}
                >
                  {suggestion.length} characters
                </Typography>
              </Box>
            ))}
          </Stack>
        </>
      )}

      <Box sx={{ mt: 4, textAlign: "center", flexShrink: 0 }}>
        <Button
          variant="outlined"
          onClick={handleRegenerateInModal}
          disabled={isGenerating}
          startIcon={
            isGenerating ? <CircularProgress size={16} /> : <Refresh />
          }
          sx={{
            borderRadius: 3,
            textTransform: "none",
            px: 3,
            height: 43,
            minWidth: isMobile ? "100%" : "320px",
          }}
        >
          {isGenerating ? "Generating..." : "Generate New Suggestions"}
        </Button>
      </Box>
    </Box>
  );

  const hasContent = currentReview && currentReview.trim().length > 0;

  return (
    <>
      <Tooltip
        title={
          rating === 0 ? "Select a star rating first to draft with AI" : ""
        }
      >
        <Box>
          <Button
            aria-label={hasContent ? "Improve with AI" : "Draft with AI"}
            variant="text"
            onClick={generateSuggestions}
            disabled={isGenerating || rating === 0}
            startIcon={
              <Box
                sx={{
                  width: 20,
                  height: 20,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {isGenerating ? (
                  <CircularProgress size={16} color="primary" />
                ) : (
                  <AutoAwesome sx={{ fontSize: 20, color: "primary.main" }} />
                )}
              </Box>
            }
            sx={{
              textTransform: "none",
              color: rating === 0 ? "text.disabled" : "text.secondary",
              backgroundColor: "transparent",
              minWidth: isMobile ? "auto" : undefined,
              "&:hover": {
                backgroundColor: "transparent",
                color: rating === 0 ? "text.disabled" : "text.primary",
              },
            }}
          >
            {!isMobile &&
              (isGenerating
                ? "Generating..."
                : hasContent
                ? "Improve with AI"
                : "Draft with AI")}
          </Button>
        </Box>
      </Tooltip>
      <ConfirmDialog
        open={showModal}
        onClose={() => setShowModal(false)}
        title={hasContent ? "Improved review" : "AI suggestions"}
        body={modalBody}
        showConfirmButton={false}
        ignoreButtonText="Close"
        showIgnoreButton={false}
        modalStyle="center"
        backdropStyle="frosted"
        customStyle={{
          maxWidth: "600px",
          width: "90vw",
          maxHeight: "85vh",
          display: "flex",
          flexDirection: "column",
        }}
        mobileMinHeight="70vh"
        disableBackdropClick={false}
        showCloseIcon={true}
      />
    </>
  );
};

export default ReviewWritingAssistant;
